'use client'

import {
  ChangeEventHandler,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Avatar } from '../Avatar'
import { Icon } from '../Icon'
import { useDialogStore } from '../dialog/FullScreenBackDrop'
import { useSearchParams } from 'next/navigation'
import { useMutation } from '@/hooks/useMutation'
import { WithT } from '@/locales/types'
import { createT } from '@/locales/index'
import { AssetApi } from '@/api/asset'
import { useAuthStore } from '@/store/auth'
import { IImage } from '@/api/interfaces/IImage'
import classNames from 'classnames'
import { usePrivilege } from '@/hooks/usePrivilege'
import { authUserImage } from '@/util/avatar'
import {
  ICommentUpdateFunction,
  ICommentCreateFunction,
} from '@/api/interfaces/IComment'

type Props = {
  parentId?: number
  parentCommentNickname?: string
  domainId: number
  commentId?: number
  referenceId?: number
  initialBody?: string | null
  initialImage?: IImage | null
  createFn?: ICommentCreateFunction
  updateFn?: ICommentUpdateFunction
  onCreateOrUpdate?: (comment: any) => void
}

export const CommentInput = ({
  parentId,
  parentCommentNickname,
  domainId,
  referenceId,
  commentId,
  initialBody,
  initialImage,
  createFn,
  updateFn,
  onCreateOrUpdate,
  translations,
}: WithT<Props>) => {
  const t = createT(translations)
  const dialog = useDialogStore()
  const auth = useAuthStore()
  const { openSignin } = usePrivilege()

  const ref = useRef<HTMLTextAreaElement>(null)
  const parentCommentNicknameRef = useRef<HTMLSpanElement>(null)

  const mutation = useMutation()
  const [loading, setLoading] = useState(false)
  const [value, setValue] = useState(initialBody || '')
  const searchParams = useSearchParams()
  const fileInput = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File>()
  const [imagePreview, setImagePreview] = useState<string | undefined>(
    initialImage?.thumbnail,
  )

  const initFocused = searchParams.has('write')

  const onPressAttachment = () => {
    if (!auth.isActive()) {
      openSignin()
      return
    }

    fileInput.current?.click()
  }

  const onChangeFile: ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!event.target.files?.length) {
      return
    }

    const { files } = event.target

    const params = []

    for (let i = 0; i < files.length; i += 1) {
      params.push({ file: files.item(i)! })
    }

    const f = files.item(0)!

    setImagePreview(URL.createObjectURL(f))
    setFile(f)

    fileInput.current!.value = ''
  }

  const onSubmit = async () => {
    if (!auth.isActive()) {
      openSignin()
      return
    }

    if (loading) {
      return
    }

    if (!value) {
      return dialog.alert({ title: t('dialog-input-comment-body') })
    }

    setLoading(true)

    try {
      let imageId

      if (file) {
        imageId = (await AssetApi.uploadImage(file)).id
      }

      if (commentId) {
        if (!file && imagePreview) {
          imageId = initialImage?.id
        }
        const res = await mutation(
          updateFn?.(commentId, domainId, { body: value, imageId, parentId }),
        )
        onCreateOrUpdate?.(res)
      } else {
        const res = await mutation(
          createFn?.(domainId, {
            body: value,
            imageId,
            parentId,
            referenceId,
          }),
        )

        onCreateOrUpdate?.(res)
      }
      setValue('')
      setFile(undefined)
      setImagePreview(undefined)
      ref.current?.blur()
    } catch (e) {}

    setLoading(false)
  }

  const setTextAreaHeight = () => {
    const element = ref.current

    if (element) {
      element.style.height = '1px'
      element.style.height = `${element.scrollHeight}px`
    }
  }

  const onRemoveImage = () => {
    setFile(undefined)
    setImagePreview(undefined)
  }

  useEffect(() => {
    if (initFocused) {
      ref.current?.focus()
    }
  }, [initFocused])

  useEffect(() => {
    setTextAreaHeight()
  }, [value])

  useEffect(() => {
    setImagePreview(initialImage?.thumbnail)
  }, [initialImage])

  useEffect(() => {
    if (parentCommentNicknameRef.current) {
      ref.current!.style.textIndent = `${
        parentCommentNicknameRef.current.clientWidth + 4
      }px`
    } else {
      ref.current!.style.textIndent = `0px`
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId, parentCommentNickname, parentCommentNicknameRef.current])

  const onClickInput = (e: MouseEvent) => {
    if (!auth.isActive()) {
      openSignin()
    }
  }

  return (
    <div className="flex items-start gap-4 w-full relative">
      <div className="flex bg-gray-50 border-gray-100 p-4 rounded-xl gap-1.5 flex-1 items-start">
        <Avatar size={24} image={authUserImage(auth.user)} />
        <div className="flex-1 flex flex-col items-start gap-3">
          {parentId && !!parentCommentNickname && (
            <span
              ref={parentCommentNicknameRef}
              className="text-body1 absolute py-0.5"
            >
              @{parentCommentNickname}
            </span>
          )}

          <textarea
            ref={ref}
            className="h-0 py-0.5 appearance-none outline-none bg-transparent placeholder:text-text-60 resize-none text-body1 px-1 w-full"
            placeholder={t('try-to-write-comment')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!auth.isActive()}
          />

          {!auth.isActive() && (
            <div
              className="absolute cursor-pointer left-0 right-0 top-0 bottom-0"
              onClick={onClickInput}
            />
          )}

          {imagePreview && (
            <div className="z-10 pc:relative pc:p-0 pc:bg-transparent pc:w-min pc:translate-y-0 absolute top-0 -translate-y-full bg-black bg-opacity-70 left-0 w-full py-4">
              <div className="flex justify-end px-4 mb-2 pc:hidden">
                <Icon name="close" size={24} onClick={onRemoveImage} />
              </div>
              {imagePreview && (
                <div className="relative flex justify-center">
                  <div className="absolute hidden pc:flex right-2 top-2 bg-black bg-opacity-40 rounded-full p-1">
                    <Icon name="close" size={20} onClick={onRemoveImage} />
                  </div>
                  <figure className="rounded overflow-hidden aspect-square flex w-32 h-32">
                    <img
                      src={imagePreview}
                      alt="preview"
                      className="object-cover"
                    />
                  </figure>
                </div>
              )}
            </div>
          )}
        </div>
        {!imagePreview && (
          <Icon name="photo" size={20} onClick={onPressAttachment} />
        )}
      </div>
      <Icon
        name="location"
        size={20}
        className={classNames('!text-key cursor-pointer mt-4 opacity-40', {
          '!opacity-100': value || imagePreview,
        })}
        onClick={onSubmit}
      />
      <input
        type="file"
        hidden
        ref={fileInput}
        onChange={onChangeFile}
        accept={'image/*'}
      />
    </div>
  )
}
